/**
 * The meshes the stages are made of, asked of the meshing worker (see mesher.ts) once each.
 * It is given one job at a time, so that each reply answers the job last sent.
 */

import type { ClayKind, HoleFacing } from "./clay.ts";
import type { IndexedMesh } from "./geometry.ts";
import type { MesherJob, MesherReply, TargetMeshes } from "./mesher.ts";

interface Job {
  message: MesherJob;
  done(reply: MesherReply): void;
  fail(error: Error): void;
}

let worker: Worker | null = null;
/** Jobs not yet given to the worker, the next first. */
const queue: Job[] = [];
/** The job the worker is on. */
let running: Job | null = null;
/** What each job comes to (see keyOf), asked once. */
const made = new Map<string, Promise<MesherReply>>();

function mesher(): Worker {
  if (worker) return worker;
  const w = new Worker(new URL("mesher.js", import.meta.url), { type: "module" });
  w.onmessage = (e: MessageEvent<MesherReply>) => {
    const job = running!;
    running = null;
    job.done(e.data);
    next();
  };
  // The worker itself could not start (its script, say): nothing asked will come.
  w.onerror = () => {
    const error = new Error("the meshing worker failed");
    for (const job of running ? [running, ...queue] : queue) job.fail(error);
    running = null;
    queue.length = 0;
    made.clear();
    worker = null;
  };
  return worker = w;
}

function next(): void {
  if (running || queue.length === 0) return;
  running = queue.shift()!;
  mesher().postMessage(running.message);
}

function keyOf(message: MesherJob): string {
  return message.kind === "target"
    ? `target ${message.request}`
    : `clay ${message.clay} ${message.holes}`;
}

/** What `message` comes to. */
function ask(message: MesherJob): Promise<MesherReply> {
  const key = keyOf(message);
  let reply = made.get(key);
  if (!reply) {
    reply = new Promise((done, fail) => queue.push({ message, done, fail }));
    made.set(key, reply);
  }
  next();
  return reply;
}

/** A request's meshes (see TargetMeshes). */
export async function targetMeshes(request: string): Promise<TargetMeshes> {
  const reply = await ask({ kind: "target", request });
  if (reply.kind === "error") throw new Error(reply.message);
  return (reply as Extract<MesherReply, { kind: "target" }>).meshes;
}

/** The starting clay of `clay`, its holes facing `holes`. */
export async function clayMesh(clay: ClayKind, holes: HoleFacing): Promise<IndexedMesh> {
  const reply = await ask({ kind: "clay", clay, holes });
  if (reply.kind === "error") throw new Error(reply.message);
  return (reply as Extract<MesherReply, { kind: "clay" }>).mesh;
}
